import {
  type Connection,
  PublicKey,
  SystemProgram,
  type TransactionInstruction,
} from '@solana/web3.js';
import BN from 'bn.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { invokeMethod } from './common.js';

export function deriveEventScheduleAddress(
  nonce: bigint,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): PublicKey {
  const bytes = Buffer.alloc(8);
  bytes.writeBigUInt64LE(nonce);
  return PublicKey.findProgramAddressSync(
    [Buffer.from('event_schedule'), programId.toBuffer(), bytes],
    programId,
  )[0];
}

export async function buildEventScheduleInstruction(
  connection: Connection,
  params: {
    admin: PublicKey;
    marketConfig: PublicKey;
    nonce: bigint;
    start: bigint;
    end: bigint;
    expected?: { start: bigint; end: bigint };
    programId?: PublicKey;
  },
): Promise<TransactionInstruction> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const eventSchedule = deriveEventScheduleAddress(params.nonce, programId);
  const adminConfig = PublicKey.findProgramAddressSync([Buffer.from('admin_config')], programId)[0];
  const value = (n: bigint): BN => new BN(n.toString());
  if (params.expected === undefined) {
    return invokeMethod(program, 'initEventSchedule', {
      scheduleNonce: value(params.nonce),
      tradingStartedTs: value(params.start),
      tradingEndTs: value(params.end),
    })
      .accounts({
        payer: params.admin,
        configAdmin: params.admin,
        adminConfig,
        marketConfig: params.marketConfig,
        eventSchedule,
        systemProgram: SystemProgram.programId,
      })
      .instruction();
  }
  return invokeMethod(
    program,
    'updateTimedMarketSchedule',
    value(params.expected.start),
    value(params.expected.end),
    value(params.start),
    value(params.end),
  )
    .accounts({
      configAdmin: params.admin,
      adminConfig,
      eventSchedule,
      eventAuthority: PublicKey.findProgramAddressSync(
        [Buffer.from('__event_authority')],
        programId,
      )[0],
      program: programId,
    })
    .instruction();
}
